/**
 * The order and the grouping of the *Nová objednávka* product grid — `FRONTEND.md` §9.82.
 *
 * The grid drew products in whatever order the API answered, with nothing saying which category any
 * of them belonged to. The owner, taking orders on it: *"I have all pizzas there, but I don't know
 * in which category… and also ordered."*
 *
 * Kept out of the page so the rule can be asserted without a browser: the page is 1 500 lines of
 * JSX and a sort order is arithmetic.
 */

/** Only what the ordering needs; both come from the generated client with far more on them. */
export interface OrderableProduct {
  id?: number
  name?: string
  categoryId?: number
  categoryName?: string
  sortOrder?: number
}

export interface OrderableCategory {
  id?: number
  name?: string
  sortOrder?: number
}

export interface ProductSection<T extends OrderableProduct = OrderableProduct> {
  /** Null for the group holding products whose category is unknown. */
  categoryId: number | null
  /** Empty when the section is not to be headed — see {@link productSections}. */
  title: string
  products: T[]
}

/** A missing sort order goes last rather than first, where `undefined` would land as 0. */
const LAST = Number.MAX_SAFE_INTEGER

function rankOf(value: number | undefined): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : LAST
}

/**
 * The menu's own order: the category as the owner arranged it, then the product within it.
 *
 * Ties break on the name and then the id, so the grid is stable — a list that reshuffles between
 * renders is one a waiter loses their place in.
 */
export function orderedProducts<T extends OrderableProduct>(
  products: readonly T[],
  categories: readonly OrderableCategory[],
): T[] {
  const categoryRank = new Map<number, number>()
  for (const category of categories) {
    if (typeof category.id === 'number') categoryRank.set(category.id, rankOf(category.sortOrder))
  }

  return [...products].sort((left, right) => {
    const leftCategory =
      typeof left.categoryId === 'number' ? (categoryRank.get(left.categoryId) ?? LAST) : LAST
    const rightCategory =
      typeof right.categoryId === 'number' ? (categoryRank.get(right.categoryId) ?? LAST) : LAST
    if (leftCategory !== rightCategory) return leftCategory - rightCategory

    const byOwnOrder = rankOf(left.sortOrder) - rankOf(right.sortOrder)
    if (byOwnOrder !== 0) return byOwnOrder

    const byName = (left.name ?? '').localeCompare(right.name ?? '', 'sk')
    if (byName !== 0) return byName
    return (left.id ?? 0) - (right.id ?? 0)
  })
}

/**
 * The grid's sections, in order.
 *
 * `heading` is false when a chip or a search has already answered *which category* — repeating it
 * above a single group is noise, and both cases collapse to the one flat grid the screen has always
 * drawn. When it is true each category gets its own heading, and a product whose category is unknown
 * falls into a trailing untitled group rather than being dropped.
 */
export function productSections<T extends OrderableProduct>(
  products: readonly T[],
  categories: readonly OrderableCategory[],
  heading: boolean,
): Array<ProductSection<T>> {
  const ordered = orderedProducts(products, categories)
  if (!heading) {
    return ordered.length === 0 ? [] : [{ categoryId: null, title: '', products: ordered }]
  }

  const names = new Map<number, string>()
  for (const category of categories) {
    if (typeof category.id === 'number') names.set(category.id, category.name ?? '')
  }

  const sections: Array<ProductSection<T>> = []
  const byCategory = new Map<number | null, ProductSection<T>>()
  for (const product of ordered) {
    const key = typeof product.categoryId === 'number' ? product.categoryId : null
    let section = byCategory.get(key)
    if (section === undefined) {
      section = {
        categoryId: key,
        // The category list is the authority on the name; `categoryName` on the product is the
        // fallback for a product whose category was deleted but whose label survived on the row.
        title: key === null ? '' : (names.get(key) ?? product.categoryName ?? ''),
        products: [],
      }
      byCategory.set(key, section)
      sections.push(section)
    }
    section.products.push(product)
  }
  return sections
}

/**
 * A React key for a product group - §9.334, FB-527.
 *
 * **The grid rendered 26 extra tiles and this is why.** The map used
 * `key={group.categoryId ?? 'nezaradene'}`, and {@link pizzaSections} gives **every** section
 * `categoryId: null` - §9.86 splits the pizza category into its five named sub-categories, and a
 * sub-category is not a category, so there is no id to carry. Five children shared one key, React
 * could not tell them apart across a re-render, and reconciliation left the previous render's nodes
 * in place beside the new ones: `Klasické | Slovenské | Talianské | Pikantné` appeared twice, which
 * is 8 + 7 + 6 + 5 = 26 tiles.
 *
 * Prefixed so the two branches cannot collide with each other either: a category whose id is `3` and
 * a sub-category literally named `3` would otherwise be the same key.
 */
export function groupKey(group: { categoryId: number | null; title: string }): string {
  return group.categoryId === null ? `t:${group.title}` : `c:${group.categoryId}`
}
